/**
 * Укладка оверлея «Тип дна» на карту MapLibre.
 *
 * Заливка кладётся ПОД рубежи (`addUnderBoundaries`): граница ИЭЗ и номер
 * района читаются сквозь грунт, а не тонут в нём.
 */
import type maplibregl from 'maplibre-gl'

import { addUnderBoundaries } from './layerOrder'
import {
  SUBSTRATE_EDGE_LAYER,
  SUBSTRATE_FILL_LAYER,
  SUBSTRATE_FILL_OPACITY,
  SUBSTRATE_SOURCE_ID,
  substrateColorExpression,
  substratePropsOf,
  type SubstrateProps,
} from './substrate'

export function applySubstrateLayers(map: maplibregl.Map, data: GeoJSON.FeatureCollection): void {
  const existing = map.getSource(SUBSTRATE_SOURCE_ID) as maplibregl.GeoJSONSource | undefined
  if (existing) {
    existing.setData(data)
    return
  }
  map.addSource(SUBSTRATE_SOURCE_ID, { type: 'geojson', data })
  const color = substrateColorExpression() as unknown as string
  addUnderBoundaries(map, {
    id: SUBSTRATE_FILL_LAYER,
    type: 'fill',
    source: SUBSTRATE_SOURCE_ID,
    paint: { 'fill-color': color, 'fill-opacity': SUBSTRATE_FILL_OPACITY },
  })
  // Кромка того же тона, плотнее заливки: соседние пятна одного класса,
  // но разных кодов, иначе сливались бы в одно.
  addUnderBoundaries(map, {
    id: SUBSTRATE_EDGE_LAYER,
    type: 'line',
    source: SUBSTRATE_SOURCE_ID,
    paint: { 'line-color': color, 'line-width': 0.6, 'line-opacity': 0.9 },
  })
}

export function removeSubstrateLayers(map: maplibregl.Map): void {
  for (const id of [SUBSTRATE_EDGE_LAYER, SUBSTRATE_FILL_LAYER]) {
    if (map.getLayer(id)) map.removeLayer(id)
  }
  if (map.getSource(SUBSTRATE_SOURCE_ID)) map.removeSource(SUBSTRATE_SOURCE_ID)
}

/** Полигон грунта под точкой — тот, что нарисован ВЕРХНИМ (выход пород лежит
 *  поверх литологии, как на листе). Слоя нет или точка вне карт — null. */
export function substrateAt(map: maplibregl.Map, lng: number, lat: number): SubstrateProps | null {
  if (!map.getLayer(SUBSTRATE_FILL_LAYER)) return null
  const hits = map.queryRenderedFeatures(map.project([lng, lat]), { layers: [SUBSTRATE_FILL_LAYER] })
  // Порядок отрисовки внутри слоя — порядок фич, а сервер ставит выходы пород
  // последними; queryRenderedFeatures отдаёт верхнюю первой.
  return substratePropsOf(hits[0]?.properties as Record<string, unknown> | undefined)
}
